import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useCart } from './context/CartContext';
import analytics from './utils/analytics';
import { setCanonicalUrl, setSEO, setRobots, PAGES_BY_PATH } from './utils/seo';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import MobileBottomNav from './components/MobileBottomNav';

// Pages — Home is bundled up front; every other page loads on demand so
// visitors don't download admin, checkout and account code they never use
import Home from './pages/Home';

const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const CheckoutSteps = lazy(() => import('./pages/CheckoutSteps'));
const Login = lazy(() => import('./pages/Login'));
const UserLogin = lazy(() => import('./pages/UserLogin'));
const Register = lazy(() => import('./pages/Register'));
const Profile = lazy(() => import('./pages/Profile'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Wallet = lazy(() => import('./pages/Wallet'));
const ReferFriend = lazy(() => import('./pages/ReferFriend'));
const Categories = lazy(() => import('./pages/Categories'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const AgeCollection = lazy(() => import('./pages/AgeCollection'));
const Blogs = lazy(() => import('./pages/Blogs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PaymentFailure = lazy(() => import('./pages/PaymentFailure'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const GiftGuide = lazy(() => import('./pages/GiftGuide'));
const GiftCards = lazy(() => import('./pages/GiftCards'));
const BulkOrders = lazy(() => import('./pages/BulkOrders'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Policy Pages
const PrivacyPolicy = lazy(() => import('./policies/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./policies/TermsConditions'));
const RefundPolicy = lazy(() => import('./policies/RefundPolicy'));
const ShippingPolicy = lazy(() => import('./policies/ShippingPolicy'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminCoupons = lazy(() => import('./pages/admin/Coupons'));
const AdminSliders = lazy(() => import('./pages/admin/Sliders'));
const AdminReels = lazy(() => import('./pages/admin/Reels'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const LoyaltySettings = lazy(() => import('./pages/admin/LoyaltySettings'));
const ReferralTracking = lazy(() => import('./pages/admin/ReferralTracking'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const AdminEnquiries = lazy(() => import('./pages/admin/Enquiries'));

// Widgets that aren't needed for first paint load separately
const CartSidebar = lazy(() => import('./components/CartSidebar'));
const WhatsAppChat = lazy(() => import('./components/WhatsAppChat'));
const LivePurchaseNotification = lazy(() => import('./components/LivePurchaseNotification'));

// Between pages: the brand rabbit drives while the next page loads
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-label="Loading">
    <img src="/logo-animated.svg" alt="" width="330" height="249" className="w-28 h-auto" />
  </div>
);

function AppContent() {
  const { isCartSidebarOpen, setIsCartSidebarOpen } = useCart();
  const location = useLocation();
  // Cart drawer code (and its confetti) is fetched the first time the cart opens
  const [cartUsed, setCartUsed] = useState(false);
  useEffect(() => { if (isCartSidebarOpen) setCartUsed(true); }, [isCartSidebarOpen]);
  // Floating widgets wait until the page has rendered and the browser is idle
  const [widgetsReady, setWidgetsReady] = useState(false);
  useEffect(() => {
    const show = () => setWidgetsReady(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(show, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(show, 2500);
    return () => clearTimeout(t);
  }, []);

  // Initialize analytics on mount
  useEffect(() => {
    analytics.init();
  }, []);

  // Track page views and set SEO on route change
  useEffect(() => {
    // Fixed-copy pages share their meta with the server renderer (src/data/pages.json).
    // Home, listing, product, age, guide and gift pages set their own richer SEO.
    const SELF_MANAGED = ['/', '/products', '/blogs', '/gift-guide', '/gift-cards', '/bulk-orders'];
    const currentSEO = SELF_MANAGED.includes(location.pathname) ? null : PAGES_BY_PATH[location.pathname];

    if (currentSEO) {
      setSEO({
        title: currentSEO.title,
        description: currentSEO.description,
        url: location.pathname
      });
    }

    // Private / transactional pages should never be indexed
    const noindex = currentSEO?.noindex
      || /^\/(cart|checkout|login|register|profile|orders|wishlist|wallet|admin|payment-failure)/.test(location.pathname)
      || /[?&]search=/.test(location.search);
    setRobots(noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical: path only, except the listing filters that are real landing pages
    const params = new URLSearchParams(location.search);
    const keep = new URLSearchParams();
    if (location.pathname === '/products') {
      ['category', 'ageGroup'].forEach(k => params.get(k) && keep.set(k, params.get(k)));
    }
    setCanonicalUrl(`${location.pathname}${keep.toString() ? `?${keep}` : ''}`);

    // Track page view in analytics
    const title = currentSEO?.title || document.title;
    analytics.trackPageView(location.pathname, title);
  }, [location]);

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Admin Routes - No Navbar/Footer */}
      <Route
        path="/admin/*"
        element={
          <Routes>
            <Route
              path="/"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/products"
              element={
                <AdminRoute>
                  <AdminProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <AdminRoute>
                  <AdminOrders />
                </AdminRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <AdminRoute>
                  <AdminCategories />
                </AdminRoute>
              }
            />
            <Route
              path="/coupons"
              element={
                <AdminRoute>
                  <AdminCoupons />
                </AdminRoute>
              }
            />
            <Route
              path="/sliders"
              element={
                <AdminRoute>
                  <AdminSliders />
                </AdminRoute>
              }
            />
            <Route
              path="/reels"
              element={
                <AdminRoute>
                  <AdminReels />
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <AdminSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <AdminRoute>
                  <AdminReports />
                </AdminRoute>
              }
            />
            <Route
              path="/loyalty-settings"
              element={
                <AdminRoute>
                  <LoyaltySettings />
                </AdminRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <AdminRoute>
                  <Analytics />
                </AdminRoute>
              }
            />
            <Route
              path="/referral-tracking"
              element={
                <AdminRoute>
                  <ReferralTracking />
                </AdminRoute>
              }
            />
            <Route
              path="/enquiries"
              element={
                <AdminRoute>
                  <AdminEnquiries />
                </AdminRoute>
              }
            />
          </Routes>
        }
      />

      {/* Public Routes - With Navbar/Footer */}
      <Route
        path="/*"
        element={
          <div className="flex flex-col min-h-screen">
            <Navbar onCartOpen={() => setIsCartSidebarOpen(true)} />
            <main className="flex-grow">
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/shop-by-age/:slug" element={<AgeCollection />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogPost />} />
                <Route path="/gift-guide" element={<GiftGuide />} />
                <Route path="/gift-cards" element={<GiftCards />} />
                <Route path="/bulk-orders" element={<BulkOrders />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/refer-friend" element={<ReferFriend />} />

                {/* Policy Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-conditions" element={<TermsConditions />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/shipping-policy" element={<ShippingPolicy />} />
                <Route path="/login" element={<UserLogin />} />
                <Route path="/admin-login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<CheckoutSteps />} />
                <Route path="/payment-failure" element={<PaymentFailure />} />
                <Route path="*" element={<NotFound />} />

                {/* Protected Routes */}
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <PrivateRoute>
                      <Orders />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <PrivateRoute>
                      <OrderDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    <PrivateRoute>
                      <Wishlist />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <PrivateRoute>
                      <Wallet />
                    </PrivateRoute>
                  }
                />
              </Routes>
              </Suspense>
            </main>
            <Footer />

            <Suspense fallback={null}>
              {cartUsed && (
                <CartSidebar
                  isOpen={isCartSidebarOpen}
                  onClose={() => setIsCartSidebarOpen(false)}
                />
              )}

              {/* Global WhatsApp Chat — hidden on product pages (ProductDetail renders its own with product name) */}
              {widgetsReady && !location.pathname.startsWith('/products/') && <WhatsAppChat />}

              {/* Global Live Purchase Notification */}
              {widgetsReady && <LivePurchaseNotification />}
            </Suspense>

            {/* App-style bottom bar on phones */}
            <MobileBottomNav onCartOpen={() => setIsCartSidebarOpen(true)} />
          </div>
        }
      />
    </Routes>
    </Suspense>
  );
}

export default AppContent;
